import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { AuthenService } from './authen.service';
import { environment } from '../../environments/environment';
import swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  // Get timeout duration from environment (in minutes), convert to milliseconds
  private readonly TIMEOUT_DURATION = environment.sessionTimeoutMinutes * 60 * 1000;

  // Debounce time for user activity (in milliseconds)
  private readonly DEBOUNCE_TIME = 1000;

  private timeoutTimer: any;
  private userActivity$: any;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authenService: AuthenService,
    private ngZone: NgZone
  ) {}

  /**
   * Start monitoring user activity and session timeout
   */
  public startWatching(): void {
    // Only start if user is logged in
    const currentUser = localStorage.getItem('currentUser');
    if (!currentUser) {
      return;
    }

    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      // Listen to user activity events
      this.userActivity$ = merge(
        fromEvent(document, 'click'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'touchstart'),
        fromEvent(document, 'scroll')
      ).pipe(
        debounceTime(this.DEBOUNCE_TIME),
        tap(() => {
          this.resetTimer();
        })
      ).subscribe();

      // Initialize the timeout timer
      this.resetTimer();
    });
  }

  /**
   * Stop watching for user activity and clear timers
   */
  public stopWatching(): void {
    if (this.userActivity$) {
      this.userActivity$.unsubscribe();
    }

    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }

    this.destroy$.next();
  }

  /**
   * Reset the timeout timer
   */
  private resetTimer(): void {
    // Clear existing timer
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }

    // Set new timer
    this.timeoutTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.handleTimeout();
      });
    }, this.TIMEOUT_DURATION);
  }

  /**
   * Handle session timeout - logout user
   */
  private handleTimeout(): void {
    console.log('Session timeout - logging out user');

    // Stop watching
    this.stopWatching();

    // Show SweetAlert before logout
    swal({
      title: 'เกิดข้อผิดพลาด!',
      html: "Session ของคุณหมดอายุ กรุณา Login อีกครั้ง",
      type: 'error',
      confirmButtonText: 'ปิด',
      allowOutsideClick: true
    }).then(c => {
      this.authenService.logout();
    });
  }

  /**
   * Manually trigger logout (useful for testing or manual logout)
   */
  public logout(): void {
    this.stopWatching();
    this.authenService.logout();
  }
}
